import {
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
} from "react";
import { toast } from "react-toastify";
import { Event, EventHook, useEvent } from "../utils/eventEmitter";
import useFormValidation from "./formValidation";
import { useAuth } from "../services/firebase/auth";
import {
  getIvrData,
  saveMultiQueueIvr,
  saveQueues,
  saveSingleQueueIvr,
} from "../services/queue.service";
import {
  AudioMessage,
  IvrMenuNode,
  IvrType,
  Queue,
  getMessagesMetadata,
  getQueues,
} from "../model/queue.model";
import { remapQueueIds } from "../model/ivrMenu.utils";
import { Setter } from "../utils/types";

export default function useIvrPageHook(): IvrHook {
  const [isLoading, setIsLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [queues, setQueues] = useState<Queue[]>([]);

  const [ivrType, setIvrType] = useState<IvrType>("");

  const [selectedSingleQueueName, setSelectedSingleQueueName] = useState("");

  const [greeting, setGreeting] = useState<AudioMessage>();
  const [error, setError] = useState<AudioMessage>();
  const [menu, setMenu] = useState<IvrMenuNode | undefined>();

  const auth = useAuth();
  const formValidation = useFormValidation();
  const onSaveEvent = useEvent();

  const onSave = async () => {
    if (formValidation.validateForm()) {
      setIsLoading(true);
      try {
        const companyId = auth.user.token.claims.company;
        const saved = await saveQueues(auth, queues);
        if (!Array.isArray(saved)) {
          throw new Error("Não foi possível salvar as filas. Tente novamente.");
        }
        switch (ivrType) {
          case "single-queue":
            const q = saved.find((v) => v.name === selectedSingleQueueName);
            if (!q) return;
            saveSingleQueueIvr(companyId, q.id);
            break;
          case "multi-queue": {
            if (!greeting) {
              throw new Error("A mensagen de áudio é obrigatória");
            }

            // Filas criadas nesta mesma sessão (via menu multinível) só
            // ganham id real do Firestore agora, em `saved` — reconcilia as
            // referências na árvore antes de enviar. Casamento por nome,
            // mesmo critério já usado acima para single-queue.
            const idMap: Record<string, string> = {};
            queues.forEach((q) => {
              if (q.tempId) {
                const match = saved.find((s) => s.name === q.name);
                if (match) idMap[q.tempId] = match.id;
              }
            });
            const resolvedMenu = menu ? remapQueueIds(menu, idMap) : menu;

            saveMultiQueueIvr(companyId, greeting, error, queues, resolvedMenu);
            break;
          }
        }
        setHasUnsavedChanges(false);
      } catch (e) {
        console.error(e);
        toast(e instanceof Error ? e.message : "Erro ao salvar. Tente novamente.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const load = async () => {
    setIsLoading(true);
    try {
      const companyId = auth.user.token.claims.company;
      const data = await getIvrData(companyId);

      if (data) {
        const queues = await getQueues(auth);
        setQueues(queues);
        setIvrType(data.type);
        const selectedSingleQueue = queues.find(
          (v) => v.id === data.singleQueueId
        );
        if (selectedSingleQueue) {
          setSelectedSingleQueueName(selectedSingleQueue.name);
        }
        if (data.type === "multi-queue") {
          const messages = await getMessagesMetadata(companyId);
          setGreeting(messages.greeting);
          setError(messages.error);
          setMenu(data.menu); // ausente em contas de 1 nível — sem migração
        }
      }
    } catch (e) {
      console.error(e);
      toast("Erro ao carregar as configurações. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formValidation,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isLoading,
    setIsLoading,
    load,
    ivrType,
    setIvrType,
    onSave,
    queues,
    setQueues,
    selectedSingleQueueName,
    setSelectedSingleQueueName,
    onSaveEvent,
    greeting,
    setGreeting,
    error,
    setError,
    menu,
    setMenu,
    // audioMessage,
  };
}

export type IvrHook = {
  formValidation: any;
  load: () => Promise<void>;
  onSave: () => Promise<void>;

  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: Setter<boolean>;

  ivrType: IvrType;
  setIvrType: Setter<IvrType>;

  isLoading: boolean;
  setIsLoading: Setter<boolean>;

  queues: Queue[];
  setQueues: Setter<Queue[]>;

  selectedSingleQueueName: string;
  setSelectedSingleQueueName: Setter<string>;

  onSaveEvent: EventHook;

  greeting: AudioMessage;
  setGreeting: Setter<AudioMessage>;

  error: AudioMessage;
  setError: Setter<AudioMessage>;

  menu: IvrMenuNode | undefined;
  setMenu: Setter<IvrMenuNode | undefined>;
  // audioMessage;
};
